// ============================================================================
// imagePrefetch — rolling window prefetch immagini volantino
// ============================================================================
// Strategia:
//   - Al boot (chiamata da src/index.js) e ad ogni cambio slide (chiamata
//     da CustomCarousel.componentDidUpdate) si prefetcha una "finestra" di
//     `ahead` pagine successive alla posizione corrente.
//   - Usiamo `new Image()` (non <link rel="prefetch">): popola la HTTP cache
//     normale, che <img> riusa con cache hit istantaneo.
//   - Set globale `_completedSet` per evitare di rifetchare la stessa URL.
//   - Coda con concorrenza MAX_CONCURRENT per non saturare il pool HTTP.
//
// Mappa slide → posizione in visualOrder (visualOrder = [index?, pages...]):
//   - Desktop (≥576px && !show_one_page): 2 item per slide.
//       slide 0 → maxVisible = 1   (mostra [order[0], order[1]])
//       slide K → maxVisible = 2K+1
//   - Mobile / show_one_page: 1 item per slide.
//       slide K → maxVisible = K
//
// Logging tag convention:
//   [PF·BOOT]  init dal boot/mount carousel
//   [PF·WIN]   apertura di una nuova finestra (cambio slide)
//   [PF·DL]    progresso download singola immagine
//   [PF·HIT]   CustomSlide rileva cache hit a mount
//   [PF·MISS]  CustomSlide non trova in cache (loader on)
//   [PF·STATS] snapshot stato globale (chiamare window.__prefetchStats())
// ============================================================================

import { serverResponse } from "../util/fakeServer";
import { isSupported } from "./utility";

const MAX_CONCURRENT = 4;

const STYLE = {
  boot: "color:#ca8a04;font-weight:bold",
  win: "color:#0ea5e9;font-weight:bold",
  dlOk: "color:#059669",
  dlErr: "color:#dc2626",
  hit: "color:#059669;font-weight:bold",
  miss: "color:#9333ea;font-weight:bold",
  stats: "color:#6b7280;font-weight:bold",
  dim: "color:#6b7280",
};

const _completedSet = new Set();
const _pendingSet = new Set();
const _queue = [];
// Map url → Image instance per poter abortire i download in volo quando
// l'utente naviga a una nuova slide e gli slot di banda servono altrove.
const _activeDownloads = new Map();
let _active = 0;
let _totalEnqueuedEver = 0;

let _visualOrderCache = null;

function _visualOrder() {
  if (_visualOrderCache) return _visualOrderCache;
  const leaflet = (serverResponse && serverResponse.leaflet) || {};
  const pages = (leaflet.pages || []).filter((p) => p.number > 0);
  const order = [];
  if (leaflet.index) order.push(leaflet.index);
  pages.forEach((p) => order.push(p));
  _visualOrderCache = order;
  return order;
}

function _isSingleSlideViewport() {
  const showOnePage =
    serverResponse && serverResponse.config && serverResponse.config.show_one_page === true;
  const isMobile = window.innerWidth < 576;
  return isMobile || showOnePage;
}

function _slideRange(slideIdx) {
  // Ritorna [firstVisible, lastVisible] — gli indici in visualOrder degli
  // item attualmente mostrati nella slide.
  const order = _visualOrder();
  const hasIndex = order.length > 0 && order[0] && order[0] !== undefined;
  // hasIndex è vero se leaflet.index esiste (visualOrder include index al [0])
  const trueHasIndex =
    serverResponse &&
    serverResponse.leaflet &&
    serverResponse.leaflet.index != null;

  if (_isSingleSlideViewport()) {
    return [slideIdx, slideIdx];
  }
  // Desktop two-page
  if (trueHasIndex) {
    // slide 0: [index, page_1] → order[0..1]
    // slide K≥1: [page_(2K-1), page_(2K)] → order[2K..2K+1]
    if (slideIdx <= 0) return [0, 1];
    return [2 * slideIdx, 2 * slideIdx + 1];
  } else {
    // slide 0: solo pages[0] → order[0..0]
    // slide K≥1: [pages[2K-1], pages[2K]] → order[2K-1..2K]
    if (slideIdx <= 0) return [0, 0];
    return [2 * slideIdx - 1, 2 * slideIdx];
  }
}

function _drain() {
  while (_active < MAX_CONCURRENT && _queue.length > 0) {
    const url = _queue.shift();
    if (_completedSet.has(url)) continue;
    _active++;
    const img = new Image();
    _activeDownloads.set(url, img);
    const startTs = performance.now ? performance.now() : Date.now();
    const filename = url.split("/").pop();
    // eslint-disable-next-line no-console
    console.log(
      `%c[PF·DL] ▶ start %c${filename}%c (active=${_active}/${MAX_CONCURRENT}, queue=${_queue.length})`,
      STYLE.dim,
      "color:#0ea5e9",
      STYLE.dim
    );
    const done = (ok) => {
      // Se nel frattempo è stato cancellato (img.src=''), saltiamo.
      if (!_activeDownloads.has(url)) return;
      _active--;
      _pendingSet.delete(url);
      _activeDownloads.delete(url);
      _completedSet.add(url);
      const elapsed = Math.round(
        (performance.now ? performance.now() : Date.now()) - startTs
      );
      // eslint-disable-next-line no-console
      console.log(
        `%c[PF·DL] ${ok ? "✓" : "✗"} %c${filename}%c (${elapsed}ms, cache=${_completedSet.size}/${_totalEnqueuedEver}, active=${_active}, queue=${_queue.length})`,
        ok ? STYLE.dlOk : STYLE.dlErr,
        ok ? "color:#059669" : "color:#dc2626",
        STYLE.dim
      );
      _drain();
    };
    img.onload = () => done(true);
    img.onerror = () => done(false);
    img.src = url;
  }
}

function _enqueue(url) {
  if (!url) return false;
  if (_completedSet.has(url) || _pendingSet.has(url)) return false;
  _pendingSet.add(url);
  _queue.push(url);
  _totalEnqueuedEver++;
  return true;
}

/**
 * Costruisce il set di URL (full + thumb) per le posizioni order[start..end].
 * Usato sia per enqueue che per "cosa NON cancellare" nella nuova finestra.
 */
function _urlsForRange(start, end) {
  const out = new Set();
  if (start > end) return out;
  const order = _visualOrder();
  const config = (serverResponse && serverResponse.config) || {};
  const versionSuffix = "?v=" + config.version;
  const realStart = Math.max(0, start);
  const realEnd = Math.min(order.length - 1, end);
  for (let i = realStart; i <= realEnd; i++) {
    const p = order[i];
    if (!p) continue;
    const fullBase = isSupported ? p.webp_image_file : p.image_file;
    if (fullBase) out.add(fullBase + versionSuffix);
    const thumbBase = isSupported
      ? p.webp_thumb_image_file
      : p.thumb_image_file;
    if (thumbBase) out.add(thumbBase + versionSuffix);
  }
  return out;
}

/**
 * Calcola gli URL essenziali per una slide: visibili + ahead + behind.
 * Sono gli URL da NON cancellare quando l'utente naviga.
 */
function _essentialUrlsForSlide(slideIdx, ahead, behind) {
  const [first, last] = _slideRange(slideIdx);
  const visible = _urlsForRange(first, last);
  const aheadSet = _urlsForRange(last + 1, last + ahead);
  const behindSet = _urlsForRange(first - behind, first - 1);
  const merged = new Set(visible);
  aheadSet.forEach((u) => merged.add(u));
  behindSet.forEach((u) => merged.add(u));
  return merged;
}

/**
 * Cancella TUTTI gli URL in volo + in coda che NON sono nella finestra
 * essenziale della slide indicata. Libera immediatamente gli slot di banda
 * per ciò che serve adesso (la nuova destinazione + le 2 prima/2 dopo).
 *
 * Per i download in volo: setta `img.src=""` che aborta la richiesta HTTP
 * nella maggior parte dei browser (Chrome/Firefox sicuri, Safari best-effort).
 * Per quelli in coda ma non ancora partiti: rimuove dalla coda.
 */
export function cancelOutsideOfWindow(slideIdx, ahead, behind, source = "?") {
  const keep = _essentialUrlsForSlide(slideIdx, ahead, behind);

  const cancelledInFlight = [];
  // Iteriamo su una copia perché modifichiamo _activeDownloads durante il loop
  const inFlightEntries = Array.from(_activeDownloads.entries());
  for (const [url, img] of inFlightEntries) {
    if (keep.has(url)) continue;
    // Detach handlers PRIMA di azzerare src per evitare callback spuri
    img.onload = null;
    img.onerror = null;
    try {
      img.src = "";
    } catch (_e) {
      /* niente */
    }
    _activeDownloads.delete(url);
    _pendingSet.delete(url);
    _active = Math.max(0, _active - 1);
    cancelledInFlight.push(url.split("/").pop());
  }

  const cancelledQueued = [];
  for (let i = _queue.length - 1; i >= 0; i--) {
    const url = _queue[i];
    if (keep.has(url)) continue;
    _pendingSet.delete(url);
    cancelledQueued.push(url.split("/").pop());
    _queue.splice(i, 1);
  }

  if (cancelledInFlight.length > 0 || cancelledQueued.length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      `%c[PF·CANCEL]%c source=${source} slide=${slideIdx} → liberati ${cancelledInFlight.length} in-flight + ${cancelledQueued.length} queued (window: visible + ahead=${ahead} + behind=${behind})`,
      "color:#dc2626;font-weight:bold",
      STYLE.dim
    );
    if (cancelledInFlight.length > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `  ✂ abort in-flight: ${cancelledInFlight.join(", ")}`
      );
    }
    if (cancelledQueued.length > 0) {
      // eslint-disable-next-line no-console
      console.log(
        `  ✂ rimossi da coda: ${cancelledQueued.join(", ")}`
      );
    }
    // Riempi gli slot liberati con la prossima coda
    _drain();
  }
}

/**
 * Verifica se un URL è già stato scaricato (HTTP cache hit garantito).
 */
export function isPrefetched(url) {
  return _completedSet.has(url);
}

/**
 * Marca manualmente un URL come "completato/in cache". Usato da CustomSlide
 * quando rileva un cache hit nativo (img.complete=true) per URL che il
 * helper non aveva tracciato (es. HEAD preload via <link>). Serve a
 * far emergere HIT consistenti sulle re-mount successive senza dover
 * ricontrollare img.complete.
 */
export function markPrefetched(url) {
  if (!url) return;
  if (_completedSet.has(url)) return;
  _completedSet.add(url);
  _pendingSet.delete(url);
}

/**
 * BOOST: porta in cima alla coda gli item della slide indicata. Caso d'uso:
 * l'utente naviga a una slide le cui pagine erano nella coda ma in fondo
 * (es. backward veloce). Spostando in cima si garantisce che siano la
 * prossima cosa scaricata appena si libera uno slot di concorrenza.
 * Se gli item non erano in coda, vengono aggiunti in cima.
 * Se sono già in flight non possiamo riprioritizzare (l'HTTP request è già
 * partito), ma il nuovo <img> nel CustomSlide farà dedup con la richiesta
 * in volo.
 */
export function prefetchNow(slideIdx, source = "now") {
  const order = _visualOrder();
  if (order.length === 0) return;
  const config = (serverResponse && serverResponse.config) || {};
  const versionSuffix = "?v=" + config.version;
  const [first, last] = _slideRange(slideIdx);

  const boosted = [];
  const newQueued = [];
  const inFlight = [];

  for (let i = first; i <= last; i++) {
    const p = order[i];
    if (!p) continue;
    const fullBase = isSupported ? p.webp_image_file : p.image_file;
    if (!fullBase) continue;
    const url = fullBase + versionSuffix;
    const filename = url.split("/").pop();
    if (_completedSet.has(url)) continue;
    if (_pendingSet.has(url)) {
      const qIdx = _queue.indexOf(url);
      if (qIdx > 0) {
        _queue.splice(qIdx, 1);
        _queue.unshift(url);
        boosted.push(filename);
      } else if (qIdx === -1) {
        // Non è in queue → è in flight (in _pendingSet ma scaricamento attivo)
        inFlight.push(filename);
      }
      // qIdx === 0 già in cima, no-op
    } else {
      _pendingSet.add(url);
      _queue.unshift(url);
      _totalEnqueuedEver++;
      newQueued.push(filename);
    }
  }

  if (boosted.length > 0 || newQueued.length > 0 || inFlight.length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      `%c[PF·NOW]%c source=${source} slide=${slideIdx} → visible=order[${first}..${last}]`,
      "color:#f59e0b;font-weight:bold",
      STYLE.dim
    );
    if (boosted.length > 0) {
      // eslint-disable-next-line no-console
      console.log(`  ⤒ boost (mossi in cima): ${boosted.join(", ")}`);
    }
    if (newQueued.length > 0) {
      // eslint-disable-next-line no-console
      console.log(`  + new (aggiunti in cima): ${newQueued.join(", ")}`);
    }
    if (inFlight.length > 0) {
      // eslint-disable-next-line no-console
      console.log(`  ⟳ già in flight (dedup browser): ${inFlight.join(", ")}`);
    }
  }
  _drain();
}

/**
 * Prefetch di una finestra intorno a `slideIdx` (ahead + behind opzionale).
 * @param {number} slideIdx - indice slide corrente nel carousel
 * @param {number} ahead - quante posizioni AVANTI prefetchare (default 4)
 * @param {string} [source] - chi chiama (per log)
 * @param {number} [behind=0] - quante posizioni INDIETRO prefetchare (per
 *   jump da indice: il delta>1 attiva behind=2 → 2 pagine prima + 4 dopo
 *   la destinazione, così back/forward nav post-jump è già caldo).
 */
export function prefetchAhead(slideIdx, ahead = 4, source = "?", behind = 0) {
  const order = _visualOrder();
  if (order.length === 0) {
    // eslint-disable-next-line no-console
    console.log(
      "%c[PF·WIN]%c source=" +
        source +
        " slide=" +
        slideIdx +
        " → visualOrder vuoto, niente da fare",
      STYLE.win,
      STYLE.dim
    );
    return;
  }

  const config = (serverResponse && serverResponse.config) || {};
  const versionSuffix = "?v=" + config.version;
  const isSingle = _isSingleSlideViewport();

  const [currentFirst, currentLast] = _slideRange(slideIdx);

  // Finestra AHEAD (item dopo gli attualmente visibili)
  const aheadStart = currentLast + 1;
  const aheadEnd = Math.min(order.length - 1, currentLast + ahead);

  // Finestra BEHIND (item prima degli attualmente visibili)
  const behindStart = Math.max(0, currentFirst - behind);
  const behindEnd = currentFirst - 1;

  const hasAhead = aheadStart <= aheadEnd;
  const hasBehind = behind > 0 && behindStart <= behindEnd;

  if (!hasAhead && !hasBehind) {
    // eslint-disable-next-line no-console
    console.log(
      `%c[PF·WIN]%c source=${source} slide=${slideIdx} → niente da prefetchare (estremi volantino)`,
      STYLE.win,
      STYLE.dim
    );
    return;
  }

  const newFull = [];
  const newThumb = [];
  const skippedAlreadyCached = [];

  // Helper per gestire un singolo indice nella visualOrder
  const processIndex = (i) => {
    const p = order[i];
    if (!p) return;
    const fullBase = isSupported ? p.webp_image_file : p.image_file;
    const thumbBase = isSupported
      ? p.webp_thumb_image_file
      : p.thumb_image_file;
    if (fullBase) {
      const url = fullBase + versionSuffix;
      const filename = url.split("/").pop();
      if (_completedSet.has(url)) {
        skippedAlreadyCached.push(filename);
      } else if (_pendingSet.has(url)) {
        // già in coda → no-op
      } else if (_enqueue(url)) {
        newFull.push(filename);
      }
    }
    if (thumbBase) {
      const url = thumbBase + versionSuffix;
      if (_enqueue(url)) newThumb.push(url.split("/").pop());
    }
  };

  // AHEAD prima (più urgente per forward nav)
  if (hasAhead) {
    for (let i = aheadStart; i <= aheadEnd; i++) processIndex(i);
  }
  // BEHIND poi (per backward nav post-jump)
  if (hasBehind) {
    for (let i = behindStart; i <= behindEnd; i++) processIndex(i);
  }

  const rangeDesc = [];
  if (hasAhead) rangeDesc.push(`ahead order[${aheadStart}..${aheadEnd}]`);
  if (hasBehind) rangeDesc.push(`behind order[${behindStart}..${behindEnd}]`);

  // eslint-disable-next-line no-console
  console.log(
    `%c[PF·WIN]%c source=${source} slide=${slideIdx} viewport=${
      isSingle ? "single" : "double"
    } visible=order[${currentFirst}..${currentLast}] → ${rangeDesc.join(", ")}`,
    STYLE.win,
    STYLE.dim
  );
  // eslint-disable-next-line no-console
  console.log(
    `%c  ├─ enqueue full:%c ${newFull.length > 0 ? newFull.join(", ") : "(nessuna)"}`,
    STYLE.dim,
    "color:#0ea5e9"
  );
  if (newThumb.length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      `%c  ├─ enqueue thumb:%c ${newThumb.length} item`,
      STYLE.dim,
      "color:#94a3b8"
    );
  }
  if (skippedAlreadyCached.length > 0) {
    // eslint-disable-next-line no-console
    console.log(
      `%c  └─ già in cache (skip):%c ${skippedAlreadyCached.join(", ")}`,
      STYLE.dim,
      STYLE.dlOk
    );
  }

  _drain();
}

/**
 * Stats globali. Da console: `window.__prefetchStats()` → tabella riassunto.
 */
export function getPrefetchStats() {
  return {
    completed: _completedSet.size,
    pending: _pendingSet.size,
    queued: _queue.length,
    active: _active,
    totalEnqueuedEver: _totalEnqueuedEver,
    completedUrls: Array.from(_completedSet).map((u) => u.split("/").pop()),
    pendingUrls: Array.from(_pendingSet).map((u) => u.split("/").pop()),
  };
}

/**
 * Snapshot leggibile sullo stato attuale.
 */
export function logPrefetchSnapshot() {
  const s = getPrefetchStats();
  // eslint-disable-next-line no-console
  console.log(
    `%c[PF·STATS]%c completed=${s.completed} pending=${s.pending} queued=${s.queued} active=${s.active}/${MAX_CONCURRENT} totalEver=${s.totalEnqueuedEver}`,
    STYLE.stats,
    STYLE.dim
  );
  // eslint-disable-next-line no-console
  if (s.completedUrls.length) console.log("  ✓ done:", s.completedUrls);
  // eslint-disable-next-line no-console
  if (s.pendingUrls.length) console.log("  … pending:", s.pendingUrls);
}

if (typeof window !== "undefined") {
  // eslint-disable-next-line no-underscore-dangle
  window.__prefetchStats = getPrefetchStats;
  // eslint-disable-next-line no-underscore-dangle
  window.__prefetchSnapshot = logPrefetchSnapshot;
}
